import React from "react";

// Material UI
import { Stack, Box, Typography, Alert } from "@mui/material";

// Styles
import { commonStyles, textStyles } from "Common/Styles";

// Components
import {
  AddNewIcon,
  CardInfo,
  FormikTextField,
  AdminSettingDeleteHeader,
  AdminSwitchSetting,
} from "Common/Components";
import ReactMarkdown from "react-markdown";

// Fields
import { locationFields, locationInformationFields } from "../Common/fields";

const LocationPage = ({ formik, viewOnly = false }) => {
  const handleAddLocation = () => {
    const newLocations = [
      ...(formik.values.appointmentLocations || []),
      {
        location: { en: "", fr: "" },
        information: { en: "", fr: "" },
        showInformationFields: false,
      },
    ];
    formik.setFieldValue("appointmentLocations", newLocations);
  };

  const handleRemoveLocation = (index) => {
    const newLocations = [...formik.values.appointmentLocations];
    newLocations.splice(index, 1);
    formik.setFieldValue("appointmentLocations", newLocations);
  };

  const handleToggleInformationFields = (index) => {
    const newLocations = [...formik.values.appointmentLocations];
    newLocations[index].showInformationFields =
      !newLocations[index].showInformationFields;
    formik.setFieldValue("appointmentLocations", newLocations);
  };

  return (
    <>
      <CardInfo
        title="Locations"
        tooltipInfo="Patients will be able to choose from these locations when booking."
        addButtonTitle={!viewOnly && "Add Location"}
        addButtonOnClick={handleAddLocation}
      >
        {formik.touched.appointmentLocations &&
          typeof formik.errors.appointmentLocations === "string" && (
            <Alert severity="error">{formik.errors.appointmentLocations}</Alert>
          )}
        {(formik.values.appointmentLocations || []).map((location, index) => (
          <Box key={index} sx={commonStyles.fieldArrayRow}>
            <AdminSettingDeleteHeader
              name="Location"
              index={index}
              onDeleteClick={() => handleRemoveLocation(index)}
              viewOnly={viewOnly}
            />
            <Stack direction="row" alignItems="flex-start" gap={2}>
              {locationFields.map((field) => (
                <FormikTextField
                  key={`${field.id}-${field.language}-${index}`}
                  formik={formik}
                  field={{
                    ...field,
                    ...(viewOnly && { disabled: true, review: true }),
                  }}
                  name={`appointmentLocations.${index}.${field.id}.${field.language}`}
                />
              ))}
            </Stack>
            <AdminSwitchSetting
              id={`showInformationFields-${index}`}
              name="Show banner during booking"
              tooltipInfo="Toggle this setting to display an informational banner above the filters when patients are booking an appointment. Markdown Supported."
              isChecked={location.showInformationFields || false}
              onChange={() => handleToggleInformationFields(index)}
              viewOnly={viewOnly}
            />
            {location.showInformationFields && (
              <Stack direction="column" alignItems="flex-start" gap={1}>
                <Stack direction="row" sx={{ width: "100%", gap: 1 }}>
                  {!viewOnly &&
                    locationInformationFields.map((field) => (
                      <FormikTextField
                        id={`${field.id}-${field.language}-${index}`}
                        key={`${field.id}-${field.language}-${index}`}
                        formik={formik}
                        field={field}
                        name={`appointmentLocations.${index}.${field.id}.${field.language}`}
                      />
                    ))}
                </Stack>

                <Typography sx={textStyles.subtitle2}>Preview</Typography>
                <Stack direction="row" sx={{ width: "100%", gap: 1 }}>
                  {locationInformationFields.map((field) => (
                    <Stack
                      direction="column"
                      key={`previewStack-${field.id}-${field.language}-${index}`}
                      sx={{ width: "50%", gap: 1 }}
                    >
                      <Typography sx={textStyles.infoText}>
                        {field.lang}
                      </Typography>
                      <Alert icon={false} sx={commonStyles.banner}>
                        <ReactMarkdown>
                          {location.information?.[field.language] ||
                            `_Start typing above to see the ${field.lang} preview..._`}
                        </ReactMarkdown>
                      </Alert>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
          </Box>
        ))}
        {!viewOnly && formik.values.appointmentLocations.length > 0 && (
          <AddNewIcon
            onClick={handleAddLocation}
            tooltipInfo="Add a new location"
          />
        )}
      </CardInfo>
    </>
  );
};

export default LocationPage;
